import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Stack,
  TableContainer,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useState, useCallback, useEffect, useRef, useContext } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { type AppState } from 'state/index'
import { useTranslation } from 'react-i18next'
import AddBoxIcon from '@mui/icons-material/AddBox'
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox'
import BarChartIcon from '@mui/icons-material/BarChart'
import PreviewIcon from '@mui/icons-material/Preview'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import SaveIcon from '@mui/icons-material/Save'
import { createLegend, updateLegend, deleteLegend } from 'api/legend'
import { palettes } from 'common/custom-legend/palettes'
import colors from 'common/colors'
import { closeModal } from 'actions/app'
import { unitConvert } from '@farmtrx/units'
import type { CropConstant } from 'state/crop.js'
import { updateLayer, setPreviewLegend } from 'actions/map'
import { type CustomLegend, type LayerInfoModel } from 'state/field'
import { setLegendType } from 'actions/field'
import { get, pick } from 'es-toolkit/compat'
import { trackEvent } from 'api/usage-tracking'
import FeatureFlagsContext from 'context/feature-flags-context'
import http from 'common/http'
import { layerInfoFetched } from 'actions/field'

import { swathParameters as swathParameterList } from 'components/swath-selector'

import EqualRangeLegend from './equal-range-legend'
import LegendHistogram from './legend-histogram'

const DEM_LAYER_NAME = 'dem'
const DEM_PARAMETER = 'dem'
const ELEVATION_KEY = 'elevation'
const DEM_RANGE = 6
const DEFAULT_DEM_STYLE = 'gradient'

const resetLegendModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'auto',
  minWidth: 200,
  maxWidth: 'min(320px, calc(100vw - 32px))',
  bgcolor: colors.white,
  boxShadow: 24,
  p: '8px 8px 0 8px',
}

const iconButtonStyle = {
  color: colors.white,
  marginRight: '3px',
}

const NumberInputStyle = {
  margin: '10px',
  '& .MuiInputBase-input': {
    fontSize: '0.875rem',
  },
  '& .MuiInputBase-root': {
    height: 30,
  },
  '& input[type=number]': {
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    MozAppearance: 'textfield',
  },
}

/**
 * Fills the panel horizontally without contributing to its intrinsic width, so
 * the form fields follow the panel rather than deciding how wide it is. Only
 * the labels and buttons are allowed to push the panel wider.
 */
const stretchWithoutGrowing = {
  width: 0,
  minWidth: '100%',
}

const InputAdornmentStyle = {
  '& .MuiInputAdornment-root': {
    fontSize: '0.875rem',
  },
  '& .MuiTypography-root': {
    fontSize: '0.875rem',
  },
}

interface EditLegendProps {
  /**
   * The base width of the component.
   * Should be a string with valid CSS units (e.g., 'px', 'rem', '%').
   * Used for positioning calculations and as the minimum width of the panel;
   * the panel grows beyond it when longer translations need the extra room.
   */
  maxWidth?: string
}

const HISTOGRAM_WIDTH = 125
const DIALOG_GAP = 8
// Upper bound on the growth allowed for longer translations; past this the
// button rows wrap and the panel grows downwards instead.
const EDIT_LEGEND_MAX_WIDTH = 560
// Space each element gives up to whatever sits outside it: the dialog keeps a
// gap to the viewport edges, its content adds the padding applied to
// .MuiDialogContent-root, and the panel also shares its row with the histogram.
const DIALOG_INSET = DIALOG_GAP * 2
const CONTENT_INSET = DIALOG_INSET + DIALOG_GAP * 2

const availableWidth = (inset: number) => `calc(100vw - ${inset}px)`

/**
 * A width that never exceeds what the viewport can actually give the element.
 * Applied to the minimums as well as the maximums, since a minimum larger than
 * the maximum wins in CSS and would push the dialog off narrow screens.
 */
const cappedWidth = (px: number, inset: number) =>
  `min(${px}px, ${availableWidth(inset)})`

const getDialogWidthPx = (width: string | number) => {
  if (typeof width === 'number') {
    return width
  }

  const match = width.match(/^(\d+)\s*px$/)
  return match ? parseInt(match[1], 10) : 300
}

export default function EditLegend(
  props: {
    harvestId: number | undefined
    layer: { name: string; translation: string } | undefined
    crop: CropConstant | undefined
    createCropLegend?: boolean
    customLegend?: CustomLegend
    layerDetails: LayerInfoModel | undefined
    createdCropLegends?: CustomLegend[]
  } & EditLegendProps,
) {
  const {
    harvestId,
    layer,
    crop,
    createCropLegend,
    customLegend,
    layerDetails,
    maxWidth = '300px',
    createdCropLegends,
  } = props

  // Get feature flags from context
  const features = useContext(FeatureFlagsContext)
  const showHillshadeColor = get(
    features,
    'dem_hillshade_color.isEnabled',
    false,
  )

  // Compute DEM style options based on feature flag
  const DEM_STYLE_OPTIONS = [
    DEFAULT_DEM_STYLE,
    ...(showHillshadeColor ? ['hillshade_color'] : []),
    'hillshade',
  ]

  const DEFAULT_RANGE = 6
  const [colorScheme, setColorScheme] = useState('')
  const [range, setRange] = useState(DEFAULT_RANGE)
  const [minRange, setMinRange] = useState(DEFAULT_RANGE)
  const [maxRange, setMaxRange] = useState(10)
  const [minMaxValues, setMinMaxValues] = useState<{
    min: number
    max: number
  }>({ min: 20.0, max: 100.0 })
  const [differenceFactor, setDifferenceFactor] = useState(0)
  const [customLegendExists, setCustomLegendExists] = useState(false)
  const [selectedCrop, setSelectedCrop] = useState<CropConstant | undefined>(
    crop,
  )
  const [selectedLayer, setSelectedLayer] = useState(layer)
  const crops = useSelector(
    (state: AppState) => state.crop.cropsWithCropVarieties,
  )

  const [layerOptions, setLayerOptions] = useState<any[]>([])

  const [saveLegendTypeChecked, setSaveLegendTypeChecked] = useState(false)
  const [legendTypeDisabled, setLegendTypeDisabled] = useState(false)
  const [useForAllDems, setUseForAllDems] = useState(false)
  const preferenceUnits = useSelector(
    (state: AppState) => state.account.singleton.prefs?.units,
  )
  const layerInfo = useSelector((state: AppState) => state.field['layerInfo'])
  const swathParameters = useSelector(
    (state: AppState) => state.map.swathParameters,
  )
  const cropLegends = useSelector(
    (state: AppState) => state.field['cropLegends'],
  )

  const [selectedSwathParameter, setSelectedSwathParameter] =
    useState<string>('yield')
  const [demLegendStyle, setDemLegendStyle] =
    useState<string>(DEFAULT_DEM_STYLE)

  const [paramPalettes, setParamPalettes] = useState<any>('')

  const [resetToDefaultDialogOpen, setResetToDefaultDialogOpen] =
    useState(false)

  const year = useSelector((state: AppState) => state.field.year)
  const [showHistogram, setShowHistogram] = useState(false)
  const legendWidthPx = getDialogWidthPx(maxWidth)
  const dialogWidthPx = legendWidthPx + HISTOGRAM_WIDTH + DIALOG_GAP
  const panelInset =
    CONTENT_INSET + (showHistogram ? HISTOGRAM_WIDTH + DIALOG_GAP : 0)

  const isDemLegendMode =
    layer?.name === DEM_LAYER_NAME ||
    selectedLayer?.name === DEM_LAYER_NAME ||
    layerDetails?.layerName === DEM_LAYER_NAME ||
    customLegend?.parameter === DEM_PARAMETER

  useEffect(() => {
    if (isDemLegendMode) {
      setShowHistogram(false)
    }
  }, [isDemLegendMode])

  useEffect(() => {
    const _layerOptions: any[] = []

    // Add DEM if editing DEM legend or if layer is DEM
    if (
      customLegend?.parameter === DEM_PARAMETER ||
      layer?.name === DEM_LAYER_NAME
    ) {
      _layerOptions.push({
        name: DEM_LAYER_NAME,
        translation: t(DEM_PARAMETER),
        parameter: DEM_PARAMETER,
      })
    }

    for (let j = 0; j < swathParameterList.length; j++) {
      _layerOptions.push({
        name: swathParameterList[j],
        translation: `${t(swathParameterList[j])}`,
        parameter: swathParameterList[j],
      })
    }

    let layerToEdit = _layerOptions[0]
    if (customLegend) {
      layerToEdit = _layerOptions.find(
        (l) => l.parameter === customLegend?.parameter,
      )
    } else if (layer) {
      if (layer.name === DEM_LAYER_NAME) {
        layerToEdit = _layerOptions.find((l) => l.name === DEM_LAYER_NAME)
      } else if (layer.name === 'swath') {
        if (harvestId) {
          const swathColumn = swathParameters.find(
            (swath) => swath.harvestId === harvestId,
          )
          const swathParameter = swathColumn?.swathParameter || 'yield'
          layerToEdit = _layerOptions.find(
            (l) => l.parameter === swathParameter,
          )
        } else {
          layerToEdit = _layerOptions.find((l) => l.name === layer?.name)
        }
      }
    }

    setSelectedLayer(layerToEdit)

    setLayerOptions(_layerOptions)
    const layerDetails = {
      layerTitle: layerToEdit?.name,
    }
    const colorPalettes = createPalettes(layerDetails, layerToEdit?.parameter)
    setParamPalettes(colorPalettes)
    if (customLegend?.legendData?.palette) {
      setColorScheme(customLegend?.legendData?.palette)
    } else {
      setColorScheme(Object.keys(colorPalettes)[0])
    }
    setDemLegendStyle(customLegend?.legendData?.styleType || DEFAULT_DEM_STYLE)
    setUseForAllDems(
      customLegend?.parameter === DEM_PARAMETER &&
        customLegend?.harvestId == null,
    )

    if (!customLegend) {
      // If neither legend exists
      setSaveLegendTypeChecked(false)
      setLegendTypeDisabled(false)
    } else if (customLegend.cropId && !customLegend.harvestId) {
      // If only crop legend exists
      setSaveLegendTypeChecked(true)
      setLegendTypeDisabled(false)
    } else if (customLegend.harvestId) {
      // If layer legend exists (possibly both)
      const hasCropLegend = cropLegends?.some(
        (legend) => legend.cropId === selectedCrop?.id,
      )
      setSaveLegendTypeChecked(false)
      setLegendTypeDisabled(!!hasCropLegend)
    }
  }, [customLegend, swathParameters, cropLegends])

  useEffect(() => {
    if (selectedCrop === undefined && crops.length > 0) {
      let crop: CropConstant = crops[0]
      if (customLegend) {
        crop = crops.find((c) => c.id === customLegend.cropId) || crops[0]
      }
      setSelectedCrop(crop)
    }
  }, [])

  const handleLayerChange = useCallback(
    (event) => {
      const _selectedLayer = event
      setSelectedLayer(_selectedLayer)
      setSelectedSwathParameter(_selectedLayer.parameter)
      const layerDetails = {
        layerTitle: _selectedLayer.name === 'yield' ? 'yield' : 'swath',
      }
      const colorPalettes = createPalettes(
        layerDetails,
        _selectedLayer.parameter,
      )

      setParamPalettes(colorPalettes)
      setRange(DEFAULT_RANGE)
      setColorScheme(Object.keys(colorPalettes)[0])
      setMinMaxRangesCropLegend(_selectedLayer.parameter)
    },
    [layerOptions],
  )

  useEffect(() => {
    let swathParameter = 'yield'
    if (harvestId) {
      const swathColumn = swathParameters.find(
        (swath) => swath.harvestId === harvestId,
      )
      swathParameter = swathColumn?.swathParameter || 'yield'
    } else if (customLegend) {
      swathParameter = customLegend?.parameter || 'yield'
    }
    setSelectedSwathParameter(swathParameter)
  }, [swathParameters])

  const [customLegendId, setCustomLegendId] = useState<number | null>(null)
  const [customLegendChanged, setCustomLegendChanged] = useState<boolean>(false)
  const [customLegendDeleted, setCustomLegendDeleted] = useState<boolean>(false)

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const anchorRef = useRef<HTMLDivElement>(null)

  const handleBtnToggle = (e) => {
    setSaveLegendTypeChecked((prev) => !prev)
    e.stopPropagation()
  }

  const handleClick = () => {
    if (isDemLegendMode) {
      saveCustomLegend(DEM_PARAMETER)
    } else if (saveLegendTypeChecked || createCropLegend !== undefined) {
      saveCustomLegend('crop')
    } else {
      saveCustomLegend('layer')
    }
  }

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return
    }
    //setOpen(false)
  }

  useEffect(() => {
    const element = document.querySelector(
      '.MuiDialog-paper',
    ) as HTMLElement | null

    let resizeObserver: ResizeObserver | undefined

    if (element) {
      const minWidth = showHistogram ? dialogWidthPx : legendWidthPx
      element.style.width = 'auto'
      element.style.minWidth = cappedWidth(minWidth, DIALOG_INSET)
      element.style.maxWidth = availableWidth(DIALOG_INSET)
      element.style.height = 'auto'
      element.style.minHeight = '0px'
      element.style.maxHeight = `calc(100vh - ${DIALOG_GAP * 2}px)`
      element.style.overflow = 'hidden'

      const targetRef = document.querySelector(
        '#legendPanel',
      ) as HTMLElement | null
      if (targetRef) {
        // Anchor the right edge of the dialog to the legend panel so that any
        // growth (histogram, longer translations) expands away from the panel.
        const anchorDialog = () => {
          const rect = targetRef.getBoundingClientRect()
          element.style.position = 'absolute'
          element.style.top = `${rect.top + window.scrollY}px`
          element.style.left = `${Math.max(
            DIALOG_GAP,
            rect.left + window.scrollX - element.offsetWidth - DIALOG_GAP,
          )}px`
          element.style.margin = '0px'
        }

        anchorDialog()

        let lastWidth = element.offsetWidth
        resizeObserver = new ResizeObserver(() => {
          if (element.offsetWidth !== lastWidth) {
            lastWidth = element.offsetWidth
            anchorDialog()
          }
        })
        resizeObserver.observe(element)
      }
    }

    const popupHeader = document.querySelector(
      '.MuiTypography-h6',
    ) as HTMLElement | null
    if (popupHeader) {
      popupHeader.style.padding = '0px'
    }

    const dialogContent = document.querySelector(
      '.MuiDialogContent-root',
    ) as HTMLElement | null
    if (dialogContent) {
      dialogContent.style.padding = '8px'
      dialogContent.style.overflowX = 'hidden'
      dialogContent.style.overflowY = 'auto'
    }

    const dialogActions = document.querySelector(
      '.MuiDialogActions-root',
    ) as HTMLElement | null
    if (dialogActions) {
      dialogActions.style.padding = '0px'
    }

    return () => resizeObserver?.disconnect()
  }, [showHistogram, legendWidthPx, dialogWidthPx])

  useEffect(() => {
    if (customLegendChanged) {
      if (layer) {
        if (layerInfo && layerInfo.length > 0) {
          const layerDetails: LayerInfoModel | undefined = layerInfo.find(
            (l) => l.harvestId === harvestId && l.layerName === layer.name,
          )
          if (layerDetails) {
            dispatch(updateLayer(layerDetails))
            dispatch(closeModal())
          }
        }
      }
    }
  }, [customLegendChanged, layerInfo])

  useEffect(() => {
    if (customLegendDeleted) {
      // Fetch fresh layer data from server - this includes global legend as fallback after harvest legend is deleted
      if (harvestId && year) {
        const params = { params: { harvest_id: harvestId, year: year } }
        http
          .get('/layer/info', params)
          .then((res: any) => {
            // Find the layer entry that matches
            const freshLayerData = res.data.find(
              (l: any) =>
                l.layerName === layer?.name && l.harvestId === harvestId,
            )

            if (freshLayerData) {
              // Update the map layer with fresh data that includes global legend
              dispatch(updateLayer(freshLayerData))
            }

            // Also dispatch the action to update Redux state
            dispatch(layerInfoFetched(res.data))
            dispatch(closeModal())
          })
          .catch((err: any) => {
            console.error('[EditLegend] Error fetching layer info:', err)
            dispatch(closeModal())
          })
      } else {
        dispatch(closeModal())
      }
    }
  }, [customLegendDeleted, harvestId, year])

  const createPalettes = (layerDetails, swathParameter) => {
    const parameter = customLegend?.parameter || swathParameter || 'yield'
    if (
      layerDetails?.layerTitle === DEM_LAYER_NAME ||
      layer?.name === DEM_LAYER_NAME
    ) {
      return { ...palettes.dem, ...palettes.general }
    }

    if (
      layerDetails?.layerTitle === 'corrected_yield' ||
      layerDetails?.layerTitle === 'yield'
    ) {
      return { ...palettes.yield, ...palettes.general }
    } else if (
      layerDetails?.layerTitle === 'swath' ||
      layerDetails === undefined
    ) {
      let p
      if (parameter === 'moisture') {
        p = palettes.moisture
      } else if (parameter === 'speed') {
        p = palettes.speed
      } else if (parameter === 'header_height') {
        p = palettes.header_height
      } else if (parameter === 'yield') {
        p = palettes.yield
      }

      return { ...p, ...palettes.general }
    } else {
      // should not get here, but just in case
      return palettes.general
    }
  }

  const setMinMaxRanges = (layerDetails, swathParameter) => {
    let minR = DEFAULT_RANGE
    let maxR = 10
    if (layerDetails?.layerTitle === DEM_LAYER_NAME) {
      minR = DEM_RANGE
      maxR = DEM_RANGE
    } else if (
      layerDetails?.layerTitle === 'swath' &&
      ['moisture', 'speed', 'header_height'].includes(swathParameter)
    ) {
      minR = DEFAULT_RANGE
      maxR = 9
    }

    setMinRange(minR)
    setMaxRange(maxR)
  }

  const setMinMaxRangesCropLegend = (parameter) => {
    let minR = DEFAULT_RANGE
    let maxR = 10

    if (parameter === DEM_PARAMETER) {
      minR = DEM_RANGE
      maxR = DEM_RANGE
    } else if (['moisture', 'speed', 'header_height'].includes(parameter)) {
      maxR = 9
    }

    setMinRange(minR)
    setMaxRange(maxR)
  }

  const getMinMaxValuesOnLoad = useCallback(() => {
    let min, max, existingLegend
    if (layerDetails || customLegend) {
      setMinMaxRanges(layerDetails, selectedSwathParameter)

      let customLegends: any[] = []
      // There are three conditions here to get the min and max values
      // 1. If there is a custom legend, we take the min and max values from it
      // 2. If there is no custom legend, but metadata is available, we take the min and max values from metadata
      // 3. If there is no custom legend and no metadata, we take the default values
      // Check if there is a custom legend
      if (customLegend) {
        existingLegend = customLegend
      } else if (
        layerDetails?.customLegends &&
        layerDetails?.customLegends.length > 0 &&
        layerDetails?.customLegends[0] !== null
      ) {
        customLegends = layerDetails.customLegends
        // For Swath layers, we need to check if there is customLegend for the selected swath parameter
        if (layerDetails.layerTitle === 'swath') {
          existingLegend = customLegends.find(
            (cl) =>
              cl.parameter === layerDetails.customLegendMetadataParam ||
              cl.parameter === selectedSwathParameter,
          )
        } else if (layerDetails.layerTitle === DEM_LAYER_NAME) {
          existingLegend = customLegends.find(
            (cl) => cl.parameter === DEM_PARAMETER,
          )
        } else {
          existingLegend = customLegends[0]
        }
      }

      if (customLegend) {
        min = parseFloat(existingLegend.legendData?.ranges?.q[1])
        max = parseFloat(
          existingLegend.legendData?.ranges?.q[
            existingLegend.legendData?.num_ranges - 1
          ],
        )
      } else if (
        layerDetails?.metadata &&
        Object.keys(layerDetails.metadata).length > 0 &&
        layerDetails.metadata?.['legend_data'] !== undefined
      ) {
        // For DEM layers, use 'elevation' key in metadata
        const legendData = layerDetails.metadata?.['legend_data']
        const paramLegendData =
          layerDetails.customLegendMetadataParam === DEM_PARAMETER
            ? legendData?.[ELEVATION_KEY]
            : legendData?.[layerDetails.customLegendMetadataParam]
        min = paramLegendData?.min
        max = paramLegendData?.max
      } else if (layerDetails?.layerName === 'swath') {
        // These are the default values when there is no custom legend and also no metadata
        ;[min, max] = getDefaultMinMaxValues(selectedSwathParameter)
      }

      setCustomLegendExists(existingLegend != null ? true : false)
      setCustomLegendId(existingLegend != null ? existingLegend.id : null)
      setRange(
        existingLegend != null
          ? existingLegend.legendData?.num_ranges
          : DEFAULT_RANGE,
      )
      const colorPalettes = createPalettes(layerDetails, selectedSwathParameter)

      setParamPalettes(colorPalettes)
      setColorScheme(
        existingLegend?.legendData?.palette ?? Object.keys(colorPalettes)[0],
      )
      setDemLegendStyle(
        existingLegend?.legendData?.styleType || DEFAULT_DEM_STYLE,
      )

      if (typeof min === 'number' && typeof max === 'number') {
        let param = 'yield'
        if (layerDetails?.layerName === DEM_LAYER_NAME) {
          param = DEM_PARAMETER
        } else if (
          layerDetails?.layerName === 'swath' ||
          layerDetails === undefined
        ) {
          param = selectedSwathParameter
        }

        min = convertParamUnits(
          param,
          min,
          getParamUnitBase(param),
          getParamUnitPref(param),
          selectedCrop?.buPerTonne ?? 1.0,
        )
        max = convertParamUnits(
          param,
          max,
          getParamUnitBase(param),
          getParamUnitPref(param),
          selectedCrop?.buPerTonne ?? 1.0,
        )

        setMinMaxValues({
          min: parseFloat(min.toFixed(2)),
          max: parseFloat(max.toFixed(2)),
        })
        setDifferenceFactor((max - min) / (range - 2))
      }
    } else if (selectedLayer) {
      setMinMaxRangesCropLegend(selectedLayer['parameter'])
      ;[min, max] = getDefaultMinMaxValues(selectedLayer['parameter'])
      min = convertParamUnits(
        selectedLayer['parameter'],
        min,
        getParamUnitBase(selectedLayer['parameter']),
        getParamUnitPref(selectedLayer['parameter']),
        selectedCrop?.buPerTonne ?? 1.0,
      )
      max = convertParamUnits(
        selectedLayer['parameter'],
        max,
        getParamUnitBase(selectedLayer['parameter']),
        getParamUnitPref(selectedLayer['parameter']),
        selectedCrop?.buPerTonne ?? 1.0,
      )
      setMinMaxValues({
        min: parseFloat(min.toFixed(2)),
        max: parseFloat(max.toFixed(2)),
      })
    }
  }, [selectedSwathParameter, selectedLayer, selectedCrop])

  const getParamUnitPref = (param) => {
    if (param === DEM_PARAMETER) {
      return preferenceUnits?.['length_in']
    } else if (param === 'moisture') {
      return ''
    } else if (param === 'speed') {
      return preferenceUnits?.['speed_in']
    } else if (param === 'header_height') {
      return preferenceUnits?.['length_in']
    } else if (param === 'yield') {
      return preferenceUnits?.['yieldunit_in']
    }
  }

  const getParamUnitBase = (param) => {
    if (param === DEM_PARAMETER) {
      return unitConvert.METERS
    } else if (param === 'moisture') {
      return 'percent'
    } else if (param === 'speed') {
      return unitConvert.KPH
    } else if (param === 'header_height') {
      return unitConvert.MM
    } else if (param === 'yield') {
      return unitConvert.G_MSQ
    }
  }

  const getDefaultMinMaxValues = (parameter) => {
    let min = 20
    let max = 100
    if (parameter === DEM_PARAMETER) {
      min = 0
      max = 100
    } else if (parameter === 'moisture') {
      // Defaults are in percentages
      min = 2
      max = 50
    } else if (parameter === 'speed') {
      // Defaults are in kph
      min = 0
      max = 10
    } else if (parameter === 'header_height') {
      // Defaults are in mm
      min = 0
      max = 300
    } else if (parameter === 'yield') {
      // Defaults are in gm/m2
      min = 50
      max = 500
    }
    return [min, max]
  }

  const convertParamUnits = (param, value, fromUnit, toUnit, buPerTonne) => {
    if (param === DEM_PARAMETER) {
      return parseFloat(
        Number(unitConvert.convertLength(value, toUnit, fromUnit)).toFixed(2),
      )
    } else if (param === 'yield') {
      return parseFloat(
        unitConvert.convertYield(value, fromUnit, toUnit, buPerTonne),
      )
    } else if (param === 'moisture') {
      return value // Moisture is already in percentage
    } else if (param === 'header_height') {
      return parseFloat(
        Number(unitConvert.convertLength(value, toUnit, fromUnit)).toFixed(2),
      )
    } else if (param === 'speed') {
      return parseFloat(
        Number(unitConvert.convertSpeed(value, toUnit, fromUnit)).toFixed(2),
      )
    }
    return value
  }

  const setCrop = useCallback(
    (crop) => {
      setSelectedCrop(crop)
    },
    [crops],
  )

  useEffect(() => {
    getMinMaxValuesOnLoad()
    if (selectedLayer) {
      if (layerDetails) {
        let customLegends: any[] = []
        if (
          layerDetails.customLegends &&
          layerDetails.customLegends.length > 0 &&
          layerDetails.customLegends[0] !== null
        ) {
          customLegends = layerDetails.customLegends
        }
      }
    }
  }, [selectedSwathParameter, customLegend, layerOptions])

  useEffect(() => {
    if (minMaxValues) {
      setDifferenceFactor((minMaxValues.max - minMaxValues.min) / (range - 2))
    }
  }, [minMaxValues, range])

  const handleRangeChange = useCallback(
    (event) => {
      if (isDemLegendMode) {
        return
      }
      const inputRange = parseInt(event.target.value)
      if (inputRange >= minRange && inputRange <= maxRange) {
        setRange(inputRange)
        setDifferenceFactor(
          (minMaxValues.max - minMaxValues.min) / (inputRange - 2),
        )
      }
    },
    [range, isDemLegendMode, minRange, maxRange, minMaxValues],
  )

  const showLegendPreview = useCallback(() => {
    dispatch(setPreviewLegend(true))
    const requestBody = createLegendRequestBody()

    if (layer) {
      requestBody.parameter = 'yield'
      if (layer.name === DEM_LAYER_NAME) {
        requestBody.parameter = DEM_PARAMETER
      } else if (layer.name === 'swath' && selectedSwathParameter) {
        requestBody.parameter = selectedSwathParameter
      }
      if (layerInfo && layerInfo.length > 0) {
        const layerDetails: LayerInfoModel | undefined = layerInfo.find(
          (l) => l.harvestId === harvestId && l.layerName === layer.name,
        )
        if (layerDetails) {
          const updatedLayer = { ...layerDetails, customLegends: [requestBody] }
          updatedLayer.customLegends = [requestBody]
          dispatch(updateLayer(updatedLayer))
        }
      }
    }
  }, [
    dispatch,
    layer,
    layerInfo,
    harvestId,
    selectedSwathParameter,
    colorScheme,
    minMaxValues,
    range,
    differenceFactor,
    preferenceUnits,
    demLegendStyle,
    paramPalettes,
    selectedCrop,
    isDemLegendMode,
  ])

  const createLegendRequestBody = () => {
    const colorsArray: string[] = []
    for (let i = 0; i < range; i++) {
      colorsArray.push(paramPalettes[colorScheme][range][i])
    }
    const requestBody = {
      parameter: 'yield', //“yield/moisture/header_height/speed”
      cropId: selectedCrop?.id || null,
      harvestId: harvestId || null,
      // Preserve the season of the legend being edited. A crop legend created by
      // hand has no season and stays year-agnostic; only generated ones are
      // scoped, and saveCustomLegend clears this for the scopes that never carry
      // a year.
      cropYear: customLegend?.cropYear ?? null,
      legendData: {
        ranges: {
          c: colorsArray,
          q: getQuantitiesArray(),
        },
        palette: colorScheme,
        num_ranges: range,
        styleType: isDemLegendMode ? demLegendStyle : undefined,
      },
    }
    return requestBody
  }

  const saveCustomLegend = useCallback(
    (type) => {
      const requestBody = createLegendRequestBody()
      if (selectedLayer) {
        if (type === DEM_PARAMETER) {
          requestBody['cropId'] = null
          requestBody['harvestId'] = useForAllDems ? null : harvestId || null
          // Elevation does not change year to year.
          requestBody['cropYear'] = null
          requestBody.parameter = DEM_PARAMETER
        } else if (type === 'layer') {
          requestBody['cropId'] = null
          // Harvest-scoped legends already imply a season.
          requestBody['cropYear'] = null
          requestBody.parameter = 'yield'
        } else if (type === 'crop') {
          requestBody['harvestId'] = null
          requestBody.parameter = 'yield'
        }
        if (type !== DEM_PARAMETER) {
          requestBody.parameter = selectedLayer.name
        }

        const isEditingHarvestDemLegend =
          type === DEM_PARAMETER &&
          customLegend?.parameter === DEM_PARAMETER &&
          customLegend?.harvestId != null

        const shouldCreateDemLegend =
          type === DEM_PARAMETER &&
          (useForAllDems || !isEditingHarvestDemLegend)

        if (customLegendExists && !shouldCreateDemLegend) {
          //@ts-ignore
          dispatch(updateLegend(customLegendId, requestBody)).then((res) => {
            if (res === 'LegendSet') {
              setCustomLegendChanged(true)
            }
          })
        } else {
          //@ts-ignore
          dispatch(createLegend(requestBody)).then((res) => {
            if (res === 'LegendSet') {
              setCustomLegendChanged(true)
              trackEvent('create-legend', {
                legendType: type,
                layer: selectedLayer?.name,
                source: harvestId !== undefined ? 'map' : 'crop-legends',
              })
            }
          })
        }
        dispatch(setPreviewLegend(false))
        dispatch(
          setLegendType({
            layerName: layerDetails?.layerName || selectedLayer?.name,
            useCustomLegend: true,
            harvestId: harvestId,
            swathParameter:
              layerDetails?.layerName === 'swath'
                ? selectedSwathParameter
                : undefined,
          }),
        )
      }
    },
    [
      range,
      minMaxValues,
      differenceFactor,
      colorScheme,
      customLegendId,
      customLegend,
      selectedSwathParameter,
      selectedCrop,
      demLegendStyle,
      isDemLegendMode,
      layerDetails,
      harvestId,
      selectedLayer,
      useForAllDems,
    ],
  )

  const handleResetToDefault = useCallback(() => {
    // In DEM mode with 'Use for all legends' unchecked, only delete the local
    // harvest-specific legend, never the global one.
    if (isDemLegendMode && !useForAllDems && customLegend?.harvestId == null) {
      const harvestSpecificLegend = layerDetails?.customLegends?.find(
        (cl) => cl?.parameter === DEM_PARAMETER && cl?.harvestId != null,
      )
      if (harvestSpecificLegend) {
        dispatch(
          deleteLegend(
            harvestSpecificLegend.id,
            layer,
            selectedSwathParameter,
          ) as any,
        ).then(() => {
          setCustomLegendDeleted(true)
        })
      } else {
        // No local legend to delete; close the modal without touching the global legend
        dispatch(closeModal())
      }
      return
    }
    dispatch(
      deleteLegend(customLegend?.id, layer, selectedSwathParameter) as any,
    ).then(() => {
      // Set flag only after the legend has been deleted from Redux state
      setCustomLegendDeleted(true)
    })
  }, [
    customLegend?.id,
    customLegend?.harvestId,
    isDemLegendMode,
    useForAllDems,
    layerDetails,
    layer,
    selectedSwathParameter,
    dispatch,
  ])

  const getQuantitiesArray = useCallback(() => {
    const isDem = isDemLegendMode || selectedLayer?.name === DEM_LAYER_NAME
    if (selectedLayer && minMaxValues && typeof differenceFactor === 'number') {
      // Determine the parameter for this layer so we can convert pref→base units.
      const param = isDemLegendMode
        ? DEM_PARAMETER
        : (selectedLayer?.['parameter'] ?? 'yield')
      const buPerTonne = selectedCrop?.buPerTonne ?? 1.0
      const convertQuantityToBase = (quantityPref: number) => {
        if (param === DEM_PARAMETER) {
          return parseFloat(
            Number(
              unitConvert.convertLength(
                quantityPref,
                getParamUnitBase(param),
                getParamUnitPref(param),
              ),
            ).toFixed(2),
          )
        }

        if (param === 'moisture') {
          return quantityPref
        }

        if (param === 'yield') {
          return parseFloat(
            unitConvert.convertYield(
              quantityPref,
              getParamUnitPref(param),
              getParamUnitBase(param),
              buPerTonne,
            ),
          )
        }

        if (param === 'header_height') {
          return parseFloat(
            Number(
              unitConvert.convertLength(
                quantityPref,
                getParamUnitBase(param),
                getParamUnitPref(param),
              ),
            ).toFixed(2),
          )
        }

        if (param === 'speed') {
          return parseFloat(
            Number(
              unitConvert.convertSpeed(
                quantityPref,
                getParamUnitBase(param),
                getParamUnitPref(param),
              ),
            ).toFixed(2),
          )
        }

        return quantityPref
      }
      // q0 is unused by non-DEM SLDs, but DEM styles reference it as the
      // first colour stop. Keep q0 in base units so the WMS request is valid.
      const quantitiesArray: number[] = [
        isDem ? convertQuantityToBase(minMaxValues.min) : 0,
      ]

      for (let i = 0; i < range; i++) {
        if (i < range - 1) {
          // minMaxValues are in preference units; convert back to base units for
          // storage in legendData.ranges.q (consumed by GeoServer env params and
          // EqualRangeLegend, both of which expect base units).
          const quantityPref = minMaxValues.min + differenceFactor * i
          const quantity = convertQuantityToBase(quantityPref)
          quantitiesArray.push(parseFloat(quantity.toFixed(2)))
        }
      }

      return quantitiesArray
    }

    return [0]
  }, [
    minMaxValues,
    differenceFactor,
    range,
    selectedLayer,
    selectedCrop,
    isDemLegendMode,
    preferenceUnits,
  ])

  const changeMinMaxValues = useCallback(
    (value, type) => {
      let numericValue
      if (value === '') {
        setMinMaxValues((prev) => ({
          ...prev,
          [type]: '',
        }))
      } else {
        numericValue = parseFloat(value)
      }
      if (numericValue === 0 || !isNaN(numericValue)) {
        setMinMaxValues((prev) => ({
          ...prev,
          [type]: numericValue !== undefined ? numericValue : '',
        }))
      }
    },
    [range],
  )

  const rangeButtons = (type) => {
    if (isDemLegendMode) {
      return null
    }

    return (
      <InputAdornment position="start">
        <IconButton
          color="primary"
          onClick={() => {
            if (type === 'min' && range <= minRange) return
            if (type === 'max' && range >= maxRange) return
            const updatedRange = type === 'min' ? range - 1 : range + 1
            setRange(updatedRange)
            setDifferenceFactor(
              (minMaxValues.max - minMaxValues.min) / (updatedRange - 2),
            )
          }}
        >
          {type === 'min' ? (
            <IndeterminateCheckBoxIcon
              color="primary"
              sx={{
                width: '15px',
                height: '15px',
              }}
            />
          ) : (
            <AddBoxIcon
              color="primary"
              sx={{
                width: '15px',
                height: '15px',
              }}
            />
          )}
        </IconButton>
      </InputAdornment>
    )
  }

  const getUnits = useCallback(() => {
    const yieldUnit = t(preferenceUnits?.['yieldunit_in'] + '_short')
    if (selectedLayer) {
      if (selectedLayer.name === DEM_LAYER_NAME) {
        return t(preferenceUnits?.['length_in'])
      } else if (selectedLayer.name === 'moisture') {
        return '%'
      } else if (selectedLayer.name === 'speed') {
        return t(preferenceUnits?.['speed_in'])
      } else if (selectedLayer.name === 'header_height') {
        return t(preferenceUnits?.['length_in'])
      }
    }
    return yieldUnit
  }, [selectedLayer])

  const renderLegendScopeField = () => {
    if (isDemLegendMode) {
      return (
        <>
          <InputLabel id="dem-style">{t('select_dem_legend_type')}</InputLabel>
          <Select
            labelId="dem-style"
            id="select-dem-style"
            value={demLegendStyle}
            label={t('select_dem_legend_type')}
            onChange={(e) => setDemLegendStyle(e.target.value)}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
              },
              '& .MuiSelect-select': {
                height: '25px',
                padding: '10px 32px 0px 8px',
              },
              '& .MuiInputBase-root': {
                height: 30,
              },
            }}
          >
            {DEM_STYLE_OPTIONS.map((item) => (
              <MenuItem
                value={item}
                key={item}
                sx={{
                  fontSize: '0.875rem',
                }}
              >
                {t(item)}
              </MenuItem>
            ))}
          </Select>
        </>
      )
    }

    if (selectedCrop != undefined && !createCropLegend) {
      return (
        <TextField
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '0.875rem',
            },
            width: 'auto',
            '& .MuiInputBase-root': {
              height: 30,
            },
          }}
          size="small"
          id="crop"
          value={t(selectedCrop.name)}
          label={t('crop')}
          disabled
        />
      )
    }

    if (selectedCrop !== undefined) {
      return (
        <>
          <InputLabel id="crop">{t('select_crop')}</InputLabel>
          <Select
            labelId="crop_select"
            id="select-crop"
            value={selectedCrop}
            label={t('select_crop')}
            onChange={(e) => setCrop(e.target.value)}
            MenuProps={{
              slotProps: { paper: { style: { width: 'min-content' } } },
            }}
            sx={{
              '& .MuiSelect-select': {
                '& svg': {
                  width: 'calc(100% / 6)',
                },
                height: '25px',
                padding: '10px 32px 0px 8px',
              },
              '& .MuiInputBase-root': {
                height: '30px',
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                lineHeight: 'normal',
                verticalAlign: 'middle',
                height: 'inherit',
              },
            }}
          >
            {crops.map((item: any) => (
              <MenuItem
                value={item}
                key={item.id}
                style={{
                  padding: '5px',
                }}
              >
                {t(item.name)}
              </MenuItem>
            ))}
          </Select>
        </>
      )
    }

    return null
  }

  const renderDemScopeCheckbox = () => {
    if (!isDemLegendMode) {
      return null
    }

    return (
      <FormControlLabel
        control={
          <Checkbox
            checked={useForAllDems}
            onChange={(event) => setUseForAllDems(event.target.checked)}
            color="primary"
            sx={{
              padding: '0px',
              marginRight: '5px',
            }}
          />
        }
        sx={{
          '.MuiTypography-root': {
            fontSize: '0.875rem',
          },
        }}
        label={t('use_for_all_dems')}
      />
    )
  }

  const renderPaletteField = () => {
    const isHillshadeMode = demLegendStyle === 'hillshade'
    return (
      <FormControl fullWidth size="small" disabled={isHillshadeMode}>
        <InputLabel id="palette">{t('select_palette')}</InputLabel>
        <Select
          labelId="palette"
          id="select-palette"
          value={colorScheme}
          label={t('select_palette')}
          onChange={(e) => setColorScheme(e.target.value)}
          disabled={isHillshadeMode}
          MenuProps={{
            slotProps: { paper: { style: { width: 'min-content' } } },
          }}
          sx={{
            '& .MuiSelect-select': {
              '& svg': {
                width: 'calc(100% / 6)',
              },
              height: '25px',
              padding: '10px 32px 0px 8px',
            },
            '& .MuiInputBase-root': {
              height: 30,
            },
          }}
        >
          {Object.keys(paramPalettes).map((item, index) => (
            <MenuItem
              value={item}
              key={index}
              style={{
                padding: '5px',
              }}
            >
              {paramPalettes?.[item]?.[DEFAULT_RANGE]?.map((color) => (
                <svg width="100%" height="15" key={color}>
                  <rect
                    width="100%"
                    height="15"
                    strokeWidth="1"
                    stroke="rgb(0, 0, 0)"
                    fill={color}
                    key={color}
                  />
                </svg>
              ))}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    )
  }

  const renderMinMaxFields = () => {
    return (
      <Stack direction="row" spacing={1} sx={{ marginTop: '10px' }}>
        <TextField
          size="small"
          type="number"
          value={minMaxValues.min}
          label={t('min')}
          sx={NumberInputStyle}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end" sx={InputAdornmentStyle}>
                  {getUnits()}
                </InputAdornment>
              ),
            },
          }}
          onChange={(e) => changeMinMaxValues(e.target.value, 'min')}
        />
        <TextField
          disabled={false}
          size="small"
          type="number"
          value={minMaxValues.max}
          label={t('max')}
          sx={NumberInputStyle}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end" sx={InputAdornmentStyle}>
                  {getUnits()}
                </InputAdornment>
              ),
            },
          }}
          onChange={(e) => changeMinMaxValues(e.target.value, 'max')}
        />
      </Stack>
    )
  }

  // Generate histogram data from layer metadata or default values
  const generateHistogramData = useCallback(() => {
    if (!selectedLayer) return []

    // Get data from layer metadata if available
    const legendData = layerDetails?.metadata?.['legend_data']
    const metadata = isDemLegendMode
      ? legendData?.[ELEVATION_KEY]
      : legendData?.[selectedSwathParameter]

    if (metadata?.histogram) {
      // Use actual histogram data from metadata
      return metadata.histogram.counts.map((count: number, index: number) => {
        const rawValue = metadata.histogram.values[index]
        const parameter =
          selectedLayer.name === DEM_LAYER_NAME
            ? DEM_PARAMETER
            : selectedLayer.name === 'corrected_yield' ||
                selectedLayer.name === 'yield' ||
                (selectedLayer.name === 'swath' &&
                  selectedSwathParameter === 'yield')
              ? 'yield'
              : selectedSwathParameter

        const convertedValue = convertParamUnits(
          parameter,
          rawValue,
          getParamUnitBase(parameter),
          getParamUnitPref(parameter),
          selectedCrop?.buPerTonne ?? 1.0,
        )

        return {
          label: `${convertedValue.toFixed(1)}`,
          count,
          value: convertedValue,
        }
      })
    }
    return []
  }, [
    selectedLayer,
    layerDetails,
    selectedSwathParameter,
    selectedCrop,
    convertParamUnits,
    getParamUnitBase,
    getParamUnitPref,
    isDemLegendMode,
  ])

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: 'stretch',
        width: 'fit-content',
        minWidth: cappedWidth(
          showHistogram ? dialogWidthPx : legendWidthPx,
          CONTENT_INSET,
        ),
        maxWidth: availableWidth(CONTENT_INSET),
      }}
    >
      {/* Histogram Panel - only occupies space when visible */}
      {showHistogram && (
        <Paper
          elevation={3}
          sx={{
            width: HISTOGRAM_WIDTH,
            minWidth: HISTOGRAM_WIDTH,
            maxWidth: HISTOGRAM_WIDTH,
            height: '100%',
            boxSizing: 'border-box',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 0,
          }}
        >
          <LegendHistogram
            data={generateHistogramData()}
            height={200}
            color={paramPalettes[colorScheme]?.[range]?.[range - 1]}
          />
        </Paper>
      )}
      {/* Main Edit Legend Panel - fixed width, histogram expands to the left */}
      <Paper
        elevation={3}
        sx={{
          p: 2,
          // Grow to fit longer translations rather than clipping them, with the
          // English layout width acting as the floor.
          width: 'fit-content',
          minWidth: cappedWidth(legendWidthPx, panelInset),
          maxWidth: cappedWidth(EDIT_LEGEND_MAX_WIDTH, panelInset),
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          height: 'auto',
        }}
      >
        <Box
          sx={{
            width: 1,
          }}
        >
          {createCropLegend && customLegend === undefined && selectedLayer ? (
            <FormControl
              fullWidth
              size="small"
              sx={{ marginBottom: '10px', ...stretchWithoutGrowing }}
            >
              <InputLabel id="layer_type">{t('select_layer')}</InputLabel>
              <Select
                labelId="layer_select"
                id="select-layer"
                value={selectedLayer}
                label={t('select_layer')}
                onChange={(e) => handleLayerChange(e.target.value)}
                MenuProps={{
                  slotProps: { paper: { style: { width: 'min-content' } } },
                }}
                sx={{
                  '& .MuiSelect-select': {
                    '& svg': {
                      width: 'calc(100% / 6)',
                    },
                    height: '25px',
                    padding: '10px 32px 0px 8px',
                  },
                  '& .MuiInputBase-root': {
                    height: 30,
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.875rem',
                  },
                }}
              >
                {layerOptions.map((item: any) => (
                  <MenuItem
                    value={item}
                    key={item.parameter}
                    style={{
                      padding: '5px',
                    }}
                  >
                    {item.translation}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}

          <div>
            <Grid
              container
              spacing={0.5}
              sx={{
                paddingTop: '5px',
                ...stretchWithoutGrowing,
              }}
            >
              <Grid>
                <Stack direction="row" spacing={2}>
                  <FormControl fullWidth>
                    <TextField
                      sx={{
                        width: 'auto',
                        '& .MuiInputBase-input': {
                          textAlign: 'center',
                          fontSize: '0.875rem',
                        },
                        '& .MuiInputBase-root': {
                          height: 30,
                        },
                      }}
                      id="select-ranges-label"
                      value={range}
                      error={range < minRange || range > maxRange}
                      label={t('select_ranges')}
                      onChange={handleRangeChange}
                      disabled={isDemLegendMode}
                      slotProps={{
                        input: {
                          startAdornment: rangeButtons('min'),
                          endAdornment: rangeButtons('max'),
                        },
                      }}
                    />
                  </FormControl>
                  <FormControl fullWidth>
                    {renderLegendScopeField()}
                  </FormControl>
                </Stack>

                <Box
                  sx={{
                    mt: 1,
                    opacity: demLegendStyle === 'hillshade' ? 0.5 : 1,
                  }}
                >
                  {renderPaletteField()}
                </Box>

                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      opacity: demLegendStyle === 'hillshade' ? 0.5 : 1,
                      pointerEvents:
                        demLegendStyle === 'hillshade' ? 'none' : 'auto',
                    }}
                  >
                    <TableContainer component={Paper}>
                      {renderMinMaxFields()}

                      {selectedLayer && (selectedCrop || isDemLegendMode) ? (
                        <EqualRangeLegend
                          key={`${selectedLayer.name}_minmax`}
                          layer={pick(selectedLayer, ['name', 'translation'])}
                          colors={
                            Object.keys(paramPalettes).length > 0
                              ? (paramPalettes?.[colorScheme]?.[range] ?? [])
                              : []
                          }
                          quantities={getQuantitiesArray()}
                          numRanges={range}
                          cropBuPerTonne={selectedCrop?.buPerTonne ?? 1.0}
                          selectedSwathParameter={selectedSwathParameter}
                        />
                      ) : null}
                    </TableContainer>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {createCropLegend === undefined ? (
              <Box sx={{ width: '100%', mt: 1 }}>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    width: '100%',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    rowGap: 1,
                  }}
                >
                  {!isDemLegendMode && (
                    <Tooltip title={t('show_histogram')} disableInteractive>
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          minWidth: 'unset',
                          width: 40,
                          padding: 0,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                        onClick={() => setShowHistogram((prev) => !prev)}
                      >
                        <BarChartIcon
                          sx={{
                            color: (theme) =>
                              theme.palette.primary.contrastText,
                          }}
                        />
                      </Button>
                    </Tooltip>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<PreviewIcon sx={iconButtonStyle} />}
                    sx={{ flex: 1, whiteSpace: 'nowrap' }}
                    onClick={() => {
                      showLegendPreview()
                    }}
                  >
                    {t('preview')}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<RestartAltIcon sx={iconButtonStyle} />}
                    sx={{ flex: 1, whiteSpace: 'nowrap' }}
                    onClick={() => setResetToDefaultDialogOpen(true)}
                    disabled={
                      !customLegendExists || customLegend?.cropId !== null
                    }
                  >
                    {t('reset')}
                  </Button>
                </Stack>
              </Box>
            ) : null}

            <Box
              sx={{
                width: '100%',
                mt: 1,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                flexWrap: 'wrap',
                rowGap: 1,
              }}
            >
              {renderDemScopeCheckbox()}
              {createCropLegend === undefined && !isDemLegendMode ? (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={saveLegendTypeChecked}
                      onChange={handleBtnToggle}
                      color="primary"
                      disabled={legendTypeDisabled}
                      sx={{
                        padding: '0px',
                        marginRight: '5px',
                      }}
                    />
                  }
                  sx={{
                    '.MuiTypography-root': {
                      fontSize: '0.875rem',
                    },
                  }}
                  label={t('save_crop_legend')}
                />
              ) : null}

              <Button
                variant="contained"
                color="primary"
                disabled={
                  isDemLegendMode
                    ? false
                    : createdCropLegends?.some(
                        (legend) =>
                          legend.cropId === selectedCrop?.id &&
                          legend.parameter === selectedLayer?.name,
                      )
                }
                onClick={handleClick}
                sx={{
                  padding: '4px',
                  minWidth: 98,
                  marginLeft: '5px',
                  whiteSpace: 'nowrap',
                  '.MuiButton-iconSizeMedium': {
                    marginLeft: '4px',
                    marginRight: '4px',
                  },
                }}
                startIcon={<SaveIcon sx={iconButtonStyle} />}
              >
                {t('save')}
              </Button>
            </Box>
          </div>
        </Box>

        <Modal
          keepMounted
          open={resetToDefaultDialogOpen}
          onClose={handleClose}
          aria-labelledby="keep-mounted-modal-title"
          aria-describedby="keep-mounted-modal-description"
          disablePortal
        >
          <Box sx={resetLegendModalStyle}>
            <Typography id="keep-mounted-modal-description">
              {t('reset_to_default_confirmation_msg')}
            </Typography>
            <Box>
              <Button onClick={() => setResetToDefaultDialogOpen(false)}>
                {t('no')}
              </Button>
              <Button onClick={handleResetToDefault}>{t('yes')}</Button>
            </Box>
          </Box>
        </Modal>
      </Paper>
    </Stack>
  )
}
